import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams, Link } from 'react-router-dom';
import {
  useGetMyTrainingCourseQuery,
  useRecordVideoProgressMutation,
  useSubmitQuizAttemptMutation,
  useGetMyTrainingVideoUrlMutation,
} from '../app/api';
import { useIsMobile } from './utils';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#b00020',
};

const HEARTBEAT_MS = 5000;
const SEEK_TOLERANCE = 0.5; // seconds — allow tiny rewinds without snapping
const COMPLETE_FRACTION = 0.95;

function formatTime(sec) {
  if (!Number.isFinite(sec)) return '0:00';
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  return `${m}:${ss}`;
}

function LockedVideoPlayer({ module, courseId, onComplete }) {
  const [getVideoUrl] = useGetMyTrainingVideoUrlMutation();
  const [recordProgress] = useRecordVideoProgressMutation();
  const videoRef = useRef(null);
  const [url, setUrl] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [duration, setDuration] = useState(0);
  const [maxWatched, setMaxWatched] = useState(module?.progress?.max_watched_sec || 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [completedLocal, setCompletedLocal] = useState(Boolean(module?.progress?.completed_at));
  const [isPlaying, setIsPlaying] = useState(false);
  const lastFlushedRef = useRef(maxWatched);
  // Live playback position. Used as the seek ceiling on replays so a user who
  // already completed the video still can't skip ahead of where the playhead
  // currently is. On first watch we cap at maxWatched instead, which is
  // looser (lets them re-skim sections they've already played).
  const playbackPosRef = useRef(0);

  // Load the presigned URL.
  useEffect(() => {
    let cancelled = false;
    getVideoUrl({ id: module.id }).unwrap().then(res => {
      if (!cancelled) setUrl(res.url);
    }).catch(err => {
      if (!cancelled) setLoadErr(err?.data?.detail || 'Could not load video.');
    });
    return () => { cancelled = true; };
  }, [module.id, getVideoUrl]);

  // Heartbeat the server with our max watched so the server can compute
  // completion even if the user closes the tab partway through.
  useEffect(() => {
    const id = setInterval(() => {
      const watched = Math.floor(maxWatched);
      if (watched > lastFlushedRef.current) {
        lastFlushedRef.current = watched;
        recordProgress({ courseId, moduleId: module.id, max_watched_sec: watched, duration_sec: Math.floor(duration) || null })
          .unwrap()
          .then(res => {
            if (res?.completed_at && !completedLocal) {
              setCompletedLocal(true);
              if (onComplete) onComplete();
            }
          })
          .catch(() => { /* swallow — next heartbeat will retry */ });
      }
    }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [maxWatched, courseId, module.id, recordProgress, completedLocal, onComplete]);

  // When the video reaches the completion threshold we flush immediately so
  // the next module unlocks without waiting for the next interval tick.
  useEffect(() => {
    if (completedLocal) return;
    if (!duration) return;
    if (maxWatched / duration >= COMPLETE_FRACTION) {
      const watched = Math.floor(maxWatched);
      lastFlushedRef.current = watched;
      recordProgress({ courseId, moduleId: module.id, max_watched_sec: watched, duration_sec: Math.floor(duration) || null })
        .unwrap()
        .then(res => {
          if (res?.completed_at) {
            setCompletedLocal(true);
            if (onComplete) onComplete();
          }
        })
        .catch(() => {});
    }
  }, [maxWatched, duration, completedLocal, courseId, module.id, recordProgress, onComplete]);

  const handleLoadedMetadata = (e) => {
    const d = e.target.duration;
    if (Number.isFinite(d) && d > 0) setDuration(d);
    // Resume from where we left off — but don't seek past max_watched.
    if (maxWatched > 0 && maxWatched < d) {
      playbackPosRef.current = maxWatched;
      e.target.currentTime = maxWatched;
    }
  };

  const handleTimeUpdate = (e) => {
    const t = e.target.currentTime;
    setCurrentTime(t);
    if (t > maxWatched) setMaxWatched(t);
    playbackPosRef.current = t;
  };

  const handleSeeking = (e) => {
    // First watch: cap at maxWatched (can re-skim sections already played).
    // Replay (already completed): cap at the live playback position so the
    // user can't fast-forward, even though they've technically watched
    // everything already.
    const t = e.target.currentTime;
    const ceiling = completedLocal ? playbackPosRef.current : maxWatched;
    if (t > ceiling + SEEK_TOLERANCE) {
      e.target.currentTime = Math.max(0, ceiling - 0.1);
    }
  };

  const handleEnded = () => {
    // The 95% threshold normally fires complete before the natural end, but
    // very short videos (or rounding errors) can land us here without it.
    if (!completedLocal) {
      const watched = Math.floor(duration);
      lastFlushedRef.current = watched;
      recordProgress({ courseId, moduleId: module.id, max_watched_sec: watched, duration_sec: Math.floor(duration) || null })
        .unwrap()
        .then(res => {
          if (res?.completed_at) {
            setCompletedLocal(true);
            if (onComplete) onComplete();
          }
        })
        .catch(() => {});
    }
  };

  const pct = duration ? Math.min(100, (maxWatched / duration) * 100) : 0;

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  };

  const rewind30 = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, v.currentTime - 30);
  };

  return (
    <div>
      {loadErr && (
        <div style={{ color: brand.red, background: 'rgba(176,0,32,0.08)', padding: '0.75rem', borderRadius: 8, marginBottom: '0.75rem' }}>
          {loadErr}
        </div>
      )}
      <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 10, background: '#000' }}>
        {url ? (
          <video
            ref={videoRef}
            src={url}
            // No native `controls` — we render a locked-down bar below the
            // video instead, so the user can't scrub, change rate, mute, or
            // download. Right-click is suppressed to hide the browser's own
            // "save / loop / playback speed" menu. PiP is disabled because
            // the floating PiP window comes with its own scrub bar.
            controlsList="nodownload noplaybackrate nofullscreen noremoteplayback"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
            onClick={togglePlay}
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
            onSeeking={handleSeeking}
            onEnded={handleEnded}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', cursor: 'pointer' }}
          />
        ) : (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="spinner-border" style={{ color: 'white' }} />
          </div>
        )}
      </div>

      {/* Locked control bar. Only play/pause and rewind 30s are exposed —
          no scrub bar means no skipping ahead, no replay-fast-forward, and
          no chance for a learner to game completion. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: brand.deepBlue, borderRadius: 8, padding: '0.5rem 0.75rem', marginTop: '0.5rem' }}>
        <button
          type="button"
          onClick={togglePlay}
          disabled={!url}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          style={{ background: brand.goldenrod, color: brand.deepBlue, border: 0, borderRadius: 6, padding: '0.35rem 0.75rem', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
        >
          <i className={`bi bi-${isPlaying ? 'pause-fill' : 'play-fill'}`} style={{ fontSize: '1rem' }} />
          {isPlaying ? 'Pause' : 'Play'}
        </button>
        <button
          type="button"
          onClick={rewind30}
          disabled={!url}
          aria-label="Rewind 30 seconds"
          title="Rewind 30 seconds"
          style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 6, padding: '0.35rem 0.7rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
        >
          <i className="bi bi-skip-backward-fill" />
          30s
        </button>
        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums', marginLeft: 'auto' }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>

      <div style={{ marginTop: '0.85rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: brand.charcoal, marginBottom: '0.35rem' }}>
          <span>{completedLocal ? <span style={{ color: brand.green, fontWeight: 700 }}><i className="bi bi-check-circle-fill" /> Watched</span> : `${Math.floor(pct)}% watched`}</span>
        </div>
        <div style={{ height: 6, background: brand.softGray, borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, background: completedLocal ? brand.green : brand.goldenrod, height: '100%', transition: 'width 0.4s' }} />
        </div>
        <p style={{ fontSize: '0.78rem', color: brand.charcoal, marginTop: '0.5rem', marginBottom: 0 }}>
          <i className="bi bi-info-circle me-1" />
          Use Pause to stop and Rewind 30s to go back. Skipping ahead isn&apos;t available.
        </p>
      </div>
    </div>
  );
}
LockedVideoPlayer.propTypes = {
  module: PropTypes.object.isRequired,
  courseId: PropTypes.number.isRequired,
  onComplete: PropTypes.func,
};

function Quiz({ module, courseId, passThreshold, onResult, onRestart }) {
  const [submitQuiz, { isLoading: isSubmitting }] = useSubmitQuizAttemptMutation();
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const questions = module?.payload?.questions || [];
  const allAnswered = useMemo(() => questions.every(q => answers[q.id] !== undefined), [questions, answers]);

  const handleSubmit = async () => {
    setError(null);
    if (!allAnswered) { setError('Answer every question before submitting.'); return; }
    try {
      const res = await submitQuiz({ courseId, moduleId: module.id, answers }).unwrap();
      setResult(res);
      // Don't notify the parent yet — that would trigger a refetch which
      // recomputes activeIdx and bounces the learner off this result page
      // before they've read it. onResult/onRestart fire only after the
      // learner clicks the explicit acknowledge button below.
    } catch (err) {
      setError(err?.data?.detail || 'Could not submit quiz.');
    }
  };

  if (result) {
    const passed = result.passed;
    // Pair each per_question entry back to its question so we can render the
    // question text alongside the right/wrong feedback. Falls back to
    // question order if the backend doesn't return per_question for any
    // reason (older API response).
    const feedbackById = {};
    (result.per_question || []).forEach(pq => { feedbackById[pq.id] = pq; });
    return (
      <div>
        {result.per_question && result.per_question.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            {questions.map((q, qi) => {
              const fb = feedbackById[q.id];
              if (!fb) return null;
              const isCorrect = fb.correct;
              return (
                <div key={q.id || qi} style={{
                  background: 'white',
                  border: `1px solid ${isCorrect ? brand.green : brand.red}`,
                  borderLeft: `4px solid ${isCorrect ? brand.green : brand.red}`,
                  borderRadius: 10,
                  padding: '0.85rem 1rem',
                  marginBottom: '0.6rem',
                }}>
                  <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.95rem', marginBottom: '0.4rem' }}>
                    <span style={{ color: brand.charcoal, marginRight: '0.4rem' }}>{qi + 1}.</span>
                    {q.text}
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                    color: isCorrect ? brand.green : brand.red,
                    fontWeight: 700, fontSize: '0.9rem',
                  }}>
                    <i className={`bi ${isCorrect ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`} style={{ marginTop: 2 }} />
                    <span>
                      {isCorrect
                        ? 'You are correct!'
                        : 'That is incorrect, please rewatch the previous video segment and try again'}
                    </span>
                  </div>
                  {isCorrect && fb.explanation && (
                    <p style={{ color: brand.charcoal, fontSize: '0.875rem', margin: '0.4rem 0 0', whiteSpace: 'pre-wrap' }}>
                      {fb.explanation}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {passed ? (
          <div style={{
            background: 'rgba(26,127,78,0.08)',
            border: `2px solid ${brand.green}`,
            borderRadius: 12, padding: '1.5rem', textAlign: 'center',
          }}>
            <i className="bi bi-patch-check-fill" style={{ fontSize: '3rem', color: brand.green, display: 'block', marginBottom: '0.5rem' }} />
            <h4 style={{ color: brand.green, fontWeight: 800, margin: 0 }}>Passed</h4>
            <p style={{ color: brand.charcoal, margin: '0.35rem 0 0', fontSize: '0.95rem' }}>
              You scored <strong>{result.score_pct}%</strong> ({result.correct} of {result.total}). Nice work — this training is complete.
            </p>
            <button
              type="button"
              onClick={() => { if (onResult) onResult(result); }}
              style={{ marginTop: '0.85rem', background: brand.green, color: 'white', border: 0, borderRadius: 8, padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}
            >
              <i className="bi bi-check2 me-1" /> Continue
            </button>
          </div>
        ) : (
          // Dedicated "you need to redo this" step. Visually heavier than the
          // pass card because the consequence (video progress wiped, full
          // rewatch + retake required) is genuinely larger.
          <div style={{
            background: 'rgba(176,0,32,0.06)',
            border: `2px solid ${brand.red}`,
            borderRadius: 12, padding: '1.5rem',
          }}>
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <i className="bi bi-arrow-clockwise" style={{ fontSize: '3rem', color: brand.red, display: 'block', marginBottom: '0.5rem' }} />
              <h4 style={{ color: brand.red, fontWeight: 800, margin: 0 }}>You&apos;ll need to redo this training</h4>
              <p style={{ color: brand.charcoal, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
                You scored <strong>{result.score_pct}%</strong> ({result.correct} of {result.total}). The pass threshold for this course is <strong>{passThreshold}%</strong>.
              </p>
            </div>
            <div style={{ background: 'white', border: `1px solid ${brand.softGray}`, borderRadius: 10, padding: '0.85rem 1.1rem', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.9rem', marginBottom: '0.5rem' }}>What happens next</div>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: brand.charcoal, fontSize: '0.9rem', lineHeight: 1.55 }}>
                <li>Your video progress has been reset back to your <strong>last completed knowledge check</strong> (or the start of the course if this was the first one).</li>
                <li>You&apos;ll need to <strong>rewatch the videos in that section</strong>.</li>
                <li>Then you can <strong>retake this knowledge check</strong>.</li>
              </ul>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => { if (onRestart) onRestart(); }}
                style={{ background: brand.red, color: 'white', border: 0, borderRadius: 8, padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}
              >
                <i className="bi bi-arrow-counterclockwise me-1" /> Back to the videos
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <h5 style={{ color: brand.deepBlue, fontWeight: 700, marginBottom: '0.5rem' }}>Knowledge check</h5>
      <p style={{ color: brand.charcoal, fontSize: '0.9rem', marginBottom: '1rem' }}>
        Pass threshold: <strong>{passThreshold}%</strong>. If you don&apos;t pass, you&apos;ll need to rewatch the video before trying again.
      </p>

      {questions.map((q, qi) => (
        <div key={q.id || qi} style={{ marginBottom: '1rem', padding: '1rem', background: 'white', border: `1px solid ${brand.softGray}`, borderRadius: 10 }}>
          <div style={{ fontWeight: 700, color: brand.deepBlue, marginBottom: '0.6rem', fontSize: '0.95rem' }}>
            <span style={{ color: brand.charcoal, marginRight: '0.4rem' }}>{qi + 1}.</span>
            {q.text}
          </div>
          {q.choices.map((c, ci) => {
            const checked = answers[q.id] === ci;
            return (
              <label
                key={ci}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                  padding: '0.45rem 0.6rem',
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: checked ? 'rgba(0,105,166,0.08)' : 'transparent',
                  border: checked ? `1px solid ${brand.tealBlue}` : '1px solid transparent',
                  marginBottom: '0.25rem',
                }}
              >
                <input
                  type="radio"
                  name={`q-${q.id || qi}`}
                  checked={checked}
                  onChange={() => setAnswers(a => ({ ...a, [q.id]: ci }))}
                  style={{ marginTop: 3 }}
                />
                <span style={{ color: brand.deepBlue, fontSize: '0.9rem' }}>{c}</span>
              </label>
            );
          })}
        </div>
      ))}

      {error && <div style={{ color: brand.red, marginBottom: '0.75rem', fontSize: '0.875rem' }}>{error}</div>}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={isSubmitting}
        style={{ background: brand.tealBlue, color: 'white', border: 0, borderRadius: 8, padding: '0.65rem 1.25rem', fontWeight: 700, fontSize: '0.95rem' }}
      >
        {isSubmitting ? 'Submitting…' : 'Submit answers'}
      </button>
    </div>
  );
}
Quiz.propTypes = {
  module: PropTypes.object.isRequired,
  courseId: PropTypes.number.isRequired,
  passThreshold: PropTypes.number.isRequired,
  onResult: PropTypes.func,
  onRestart: PropTypes.func,
};

export default function TrainingCourseLearner() {
  const { courseId } = useParams();
  const isMobile = useIsMobile();
  const id = Number(courseId);
  const { data: course, isLoading, refetch } = useGetMyTrainingCourseQuery({ id }, { skip: !id });

  const modules = course?.modules || [];

  // The "active" module is the first one with no completed_at. If all are
  // complete we fall through to the wrap-up view.
  const activeIdx = useMemo(() => {
    return modules.findIndex(m => !m.progress?.completed_at);
  }, [modules]);

  const [viewingIdx, setViewingIdx] = useState(0);

  // Default viewing to the active module whenever the course refetches, but
  // don't clobber the user's choice if they've navigated back to review a
  // completed earlier module.
  useEffect(() => {
    if (activeIdx >= 0) setViewingIdx(activeIdx);
    else setViewingIdx(Math.max(0, modules.length - 1));
  }, [activeIdx, modules.length]);

  if (isLoading || !course) {
    return (
      <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner-border" style={{ color: brand.tealBlue }} />
      </div>
    );
  }

  const allDone = activeIdx === -1 && modules.length > 0;
  const viewing = modules[viewingIdx] || modules[0];
  const passThreshold = course.pass_threshold || 80;
  const completedCount = modules.filter(m => m.progress?.completed_at).length;
  const overallPct = modules.length ? Math.round((completedCount / modules.length) * 100) : 0;

  const handleVideoComplete = () => { refetch(); };
  const handleQuizResult = () => { refetch(); };
  // After a failed quiz, the learner clicks "Back to the videos". We just
  // refetch — the wipe only resets progress back to the last passed quiz,
  // and the activeIdx → viewingIdx useEffect will navigate the learner to
  // the first module they need to rewatch. We deliberately don't set
  // viewingIdx to 0 here; that caused a transient flash to module 0
  // before the useEffect ran.
  const handleQuizRestart = () => {
    refetch();
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '1rem' : '1.5rem' }}>

      <Link to="/training" style={{ color: brand.tealBlue, fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}>
        <i className="bi bi-chevron-left" /> All trainings
      </Link>

      {course.status === 'sandbox' && (
        <div style={{
          background: 'repeating-linear-gradient(135deg, rgba(208,155,44,0.18) 0 14px, rgba(208,155,44,0.30) 14px 28px)',
          border: `2px solid ${brand.goldenrod}`,
          borderRadius: 12,
          padding: isMobile ? '0.75rem 0.9rem' : '0.85rem 1.1rem',
          marginTop: '0.75rem',
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        }}>
          <i className="bi bi-flask" style={{ color: brand.goldenrod, fontSize: '1.5rem', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Sandbox preview
            </div>
            <div style={{ color: brand.deepBlue, fontSize: '0.825rem', marginTop: 2 }}>
              You&apos;re seeing this course as part of a Compliance pilot. It isn&apos;t the live training yet — feedback welcome.
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{
        background: brand.deepBlue,
        borderRadius: '12px',
        padding: isMobile ? '1.25rem 1rem' : '1.5rem 1.75rem',
        marginTop: '0.75rem',
        marginBottom: isMobile ? '1rem' : '1.5rem',
        position: 'relative',
        overflow: 'hidden',
        border: course.status === 'sandbox' ? `2px solid ${brand.goldenrod}` : 'none',
      }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 0.75rem', fontSize: isMobile ? '1.15rem' : '1.5rem', lineHeight: 1.2 }}>{course.title}</h3>
        {course.description && (
          <p style={{ color: 'rgba(255,255,255,0.75)', margin: '0 0 0.85rem', fontSize: isMobile ? '0.85rem' : '0.9rem' }}>{course.description}</p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.2)', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{ width: `${overallPct}%`, height: '100%', background: brand.goldenrod, transition: 'width 0.4s' }} />
          </div>
          <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', fontWeight: 700, flexShrink: 0 }}>
            {completedCount} / {modules.length} module{modules.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {allDone ? (
        <div style={{ background: 'white', borderRadius: 12, padding: '2rem', textAlign: 'center', boxShadow: '0 2px 12px rgba(10,41,71,0.08)' }}>
          <i className="bi bi-patch-check-fill" style={{ fontSize: '3.5rem', color: brand.green, display: 'block', marginBottom: '0.5rem' }} />
          <h4 style={{ color: brand.green, fontWeight: 800, margin: 0 }}>Training complete</h4>
          <p style={{ color: brand.charcoal, margin: '0.5rem 0 0' }}>
            Nice work — you&apos;ve finished <strong>{course.title}</strong>.
          </p>
        </div>
      ) : (
        <div className="row g-3">
          {/* Module list */}
          <div className="col-12 col-lg-4">
            <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden' }}>
              <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.65rem 1rem' }}>
                <h6 style={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>Modules</h6>
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {modules.map((m, mi) => {
                  const done = Boolean(m.progress?.completed_at);
                  const isActive = mi === activeIdx;
                  const isLocked = mi > activeIdx && activeIdx !== -1;
                  const isViewing = mi === viewingIdx;
                  const clickable = !isLocked;
                  return (
                    <li
                      key={m.id}
                      onClick={() => clickable && setViewingIdx(mi)}
                      onKeyDown={(e) => {
                        if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          setViewingIdx(mi);
                        }
                      }}
                      role={clickable ? 'button' : undefined}
                      tabIndex={clickable ? 0 : -1}
                      style={{
                        padding: '0.7rem 1rem',
                        borderBottom: mi < modules.length - 1 ? `1px solid ${brand.softGray}` : 'none',
                        background: isViewing ? 'rgba(0,105,166,0.06)' : 'white',
                        cursor: clickable ? 'pointer' : 'not-allowed',
                        opacity: isLocked ? 0.55 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.6rem',
                      }}
                    >
                      <i
                        className={`bi bi-${done ? 'check-circle-fill' : isLocked ? 'lock-fill' : isActive ? 'play-circle-fill' : 'circle'}`}
                        style={{ color: done ? brand.green : isLocked ? brand.charcoal : isActive ? brand.tealBlue : brand.charcoal, fontSize: '1.05rem', flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {m.title || (m.kind === 'video' ? 'Video' : 'Quiz')}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: brand.charcoal, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {m.kind}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Active module */}
          <div className="col-12 col-lg-8">
            <div style={{ background: viewing?.kind === 'quiz' ? brand.softGray : 'white', borderRadius: 12, boxShadow: viewing?.kind === 'quiz' ? 'none' : '0 2px 12px rgba(10,41,71,0.08)', padding: viewing?.kind === 'quiz' ? 0 : (isMobile ? '0.85rem' : '1.25rem') }}>
              {viewing?.kind === 'video' && (
                <>
                  <h5 style={{ color: brand.deepBlue, fontWeight: 700, marginBottom: '0.75rem', fontSize: '1.05rem' }}>{viewing.title || 'Video'}</h5>
                  <LockedVideoPlayer
                    key={viewing.id}
                    module={viewing}
                    courseId={id}
                    onComplete={handleVideoComplete}
                  />
                </>
              )}
              {viewing?.kind === 'quiz' && (
                <div style={{ background: 'white', padding: isMobile ? '1rem' : '1.25rem', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)' }}>
                  <Quiz
                    key={viewing.id}
                    module={viewing}
                    courseId={id}
                    passThreshold={passThreshold}
                    onResult={handleQuizResult}
                    onRestart={handleQuizRestart}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
